import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/pageHeader";
import {
  Table,
  Button,
  Tabs,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Typography,
} from "antd";
import {
  ClearOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import AuthChecker from "../../components/authChecker";
import roleAndPermission from "../../constants/permissions";
import endpoints from "../../constants/endpoints";
import requests from "../../utilities/api";
import EditCMSPage from "./EditCMSPage";
import { SERVICE_DEFAULT_SLUG, SERVICE_GROUP } from "./builder/constant";
import clearSiteCache from "../../utilities/clearSiteCache";
import { getCMSColumns } from "../../config/settings/cmsPages";

const { Text } = Typography;

const CMS_TABS = [
  { key: "page", label: "Pages" },
  { key: "blogs", label: "Blogs" },
  { key: "header", label: "Header" },
  { key: "footer", label: "Footer" },
  { key: "services", label: "Services" },
  { key: "system", label: "System Pages" },
];

// Unknown / legacy groups (e.g. the removed "all" tab) fall back to Pages.
const toTabKey = (group) => {
  const g = String(group || "").toLowerCase();
  if (g === "blog" || g === "blog_page") return "blogs";
  return CMS_TABS.some((t) => t.key === g) ? g : "page";
};

const CMSlist = () => {
  const [listLoading, setListLoading] = useState(false);
  const [singlePageLoading, setSinglePageLoading] = useState(false);
  const [mode, setMode] = useState("");
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [formFor, setFormFor] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return toTabKey(
      params.get("group") ||
        (window.location.pathname.includes("/blog") ? "blogs" : "page"),
    );
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [clearingCache, setClearingCache] = useState(null); // record id, or "all"

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const getCMSPages = async (
    page = currentPage,
    limit = pageSize,
    group = activeTab,
    search = searchTerm,
  ) => {
    try {
      const api = requests();
      setListLoading(true);

      let queryGroup = group;
      if (group === "all") {
        queryGroup = undefined;
      } else if (group === "blogs") {
        queryGroup = "blogs";
      } else if (group === "page") {
        queryGroup = "page";
      }

      const queryParams = {
        limit,
        page,
        ...(queryGroup ? { "filters[group][$eqc]": queryGroup } : {}),
        ...(search ? { search } : {}),
      };

      const response = await api.get(
        endpoints.get_cms_pages_list,
        queryParams,
        true,
      );

      const body = response?.data;
      const rawList = Array.isArray(body) ? body : body?.data || [];
      const totalCount = Number(body?.total ?? rawList.length) || 0;

      // Asked for a page past the end (e.g. after a bigger page size or a delete): go to the last page.
      const lastPage = Math.max(1, Math.ceil(totalCount / limit));
      if (rawList.length === 0 && totalCount > 0 && page > lastPage) {
        setCurrentPage(lastPage);
        return;
      }

      const sortedPages = (Array.isArray(rawList) ? rawList : []).sort((a, b) =>
        (a.title || "").localeCompare(b.title || ""),
      );

      setPages(sortedPages);
      setTotal(totalCount);
    } catch (error) {
      console.error(error);
      message.error("Failed to load CMS pages.");
    } finally {
      setListLoading(false);
    }
  };

  const parseMaybeJson = (value) => {
    if (typeof value !== "string") return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  // A category's service page that doesn't exist yet opens as an unsaved copy
  // of the default service page; the first save creates the category's own
  // page. Returns false when there is no default page to copy.
  const startFromServiceDefault = async (pageSlug, pageTitle) => {
    let template = null;
    try {
      const api = requests();
      const response = await api.get(
        endpoints.get_cms_page_by_slug,
        { slug: SERVICE_DEFAULT_SLUG, group: SERVICE_GROUP },
        true,
      );
      template = parseMaybeJson(response?.data);
    } catch (error) {
      // No default page yet: the editor opens empty.
    }
    if (!template || typeof template !== "object") return false;

    const langBlock = (block) => {
      const parsed = parseMaybeJson(block);
      return parsed ? { ...parsed, data: parseMaybeJson(parsed.data) } : null;
    };
    const newTitle = pageTitle || template.title || pageSlug;
    setTitle(newTitle);
    setSlug(pageSlug);
    setFormFor({
      title: newTitle,
      slug: pageSlug,
      group: SERVICE_GROUP,
      status: "ACTIVE",
      en: langBlock(template.en),
      bn: langBlock(template.bn),
      // Copies without ids, so saving creates new SEO rows for this page. Its
      // {{category_…}} variables stay, so the SEO follows the category.
      seo_setting: (Array.isArray(template.seo_setting)
        ? template.seo_setting
        : []
      ).map(({ id, ...seo }) => seo),
    });
    message.info(
      "This category has no page yet. It starts from the default service page; save to create it.",
    );
    return true;
  };

  const getCMSSinglePages = async (idOrSlug) => {
    try {
      setSinglePageLoading(true);
      const api = requests();

      let targetId = idOrSlug;
      let resolved = false;
      const normSlug = String(idOrSlug).toLowerCase().trim();
      let matched = pages.find(
        (p) =>
          String(p.id) === String(idOrSlug) ||
          String(p.slug || "")
            .toLowerCase()
            .trim() === normSlug,
      );

      if (matched && matched.id) {
        resolved = true;
        targetId = matched.id;
        setTitle(matched.title || matched.slug);
        setSlug(matched.slug);
      } else {
        const res = await api.get(
          endpoints.get_cms_pages_list,
          { limit: 100 },
          true,
        );
        const rawList = res?.data?.data || res?.data || res || [];
        if (Array.isArray(rawList)) {
          setPages(rawList);
          const found = rawList.find(
            (p) =>
              String(p.slug || "")
                .toLowerCase()
                .trim() === normSlug || String(p.id) === String(idOrSlug),
          );
          if (found) {
            resolved = true;
            targetId = found.id;
            setTitle(found.title || found.slug);
            setSlug(found.slug);
          }
        }
      }

      if (!resolved) {
        // The list above holds at most 100 pages; ask for this slug directly.
        try {
          const query = new URLSearchParams(window.location.search);
          const groupParam = query.get("group");
          const payload = { slug: idOrSlug };
          if (groupParam) payload.group = groupParam;

          const bySlug = await api.get(
            endpoints.get_cms_page_by_slug,
            payload,
            true,
          );
          const record = parseMaybeJson(bySlug?.data);
          if (record?.id) {
            resolved = true;
            targetId = record.id;
            setTitle(record.title || record.slug);
            setSlug(record.slug);
          }
        } catch (error) {
          // Not found.
        }
      }

      if (!resolved) {
        const query = new URLSearchParams(window.location.search);
        const isNewServicePage =
          query.get("group") === SERVICE_GROUP &&
          normSlug !== SERVICE_DEFAULT_SLUG;
        if (
          isNewServicePage &&
          (await startFromServiceDefault(idOrSlug, query.get("title")))
        ) {
          return;
        }
        if (query.get("group") === SERVICE_GROUP) {
          // No default page yet: an empty editor that creates the page on save.
          setTitle(query.get("title") || idOrSlug);
          setSlug(idOrSlug);
          setFormFor({
            slug: idOrSlug,
            group: SERVICE_GROUP,
            title: query.get("title") || idOrSlug,
          });
          return;
        }
      }

      if (!targetId) {
        setFormFor(null);
        return;
      }

      const response = await api.get(
        endpoints.get_cms_page.replace("id", targetId),
        {},
        true,
      );

      const pageData = response?.data || response;
      if (
        response?.status === true ||
        response?.status === "Success" ||
        pageData
      ) {
        setFormFor(pageData);
      } else {
        setFormFor(null);
        message.error(response?.message || "Could not fetch page details.");
      }
    } catch (error) {
      console.error(error);
      message.error("Error loading page details.");
      setFormFor(null);
    } finally {
      setSinglePageLoading(false);
    }
  };

  // What /api/revalidate needs to clear one record on the website.
  const cacheTarget = (record) => ({
    slug: record?.slug,
    group: String(record?.group || "").toLowerCase(),
  });

  // Runs after every change; the admin only hears about it when it fails.
  const clearCacheAfterChange = async (target) => {
    if (!(await clearSiteCache(target))) {
      message.warning(
        'Saved, but the website cache couldn\'t be cleared. Use "Clear cache" to retry.',
      );
    }
  };

  const handleClearCache = async (record) => {
    setClearingCache(record ? record.id : "all");
    const cleared = await clearSiteCache(
      record ? cacheTarget(record) : { all: true },
    );
    setClearingCache(null);
    if (cleared) {
      message.success(
        record
          ? `Website cache cleared for /${record.slug}.`
          : "Website cache cleared for all pages.",
      );
    } else {
      message.error("Couldn't clear the website cache. Please try again.");
    }
  };

  const handleToggleStatus = async (record) => {
    const { id, status: currentStatus } = record;
    try {
      const api = requests();
      const newStatus = currentStatus === "ACTIVE" ? "DRAFT" : "ACTIVE";
      const response = await api.put(
        endpoints.update_cms_page_status.replace("id", id),
        { status: newStatus },
        true,
      );
      if (
        response?.status === true ||
        response?.status === "Success" ||
        response
      ) {
        message.success(`Status updated to ${newStatus}`);
        getCMSPages();
        clearCacheAfterChange(cacheTarget(record));
      } else {
        message.error(response?.message || "Failed to update status.");
      }
    } catch (error) {
      console.error(error);
      message.error("Error updating status.");
    }
  };

  const editLayout = async (
    data,
    updatedTitle,
    seoSetting,
    fullSlug,
    group,
    status = "ACTIVE",
    currentLang = "en",
  ) => {
    try {
      const api = requests();
      let pageId = formFor?.id || formFor?.data?.id;
      const targetSlug = fullSlug || slug;

      // If the fetched data has a different slug (e.g. categories/default) than the one being edited,
      // we are creating a new page from a template, so don't update the template's ID.
      if (formFor?.slug && targetSlug && formFor.slug !== targetSlug) {
        pageId = null;
      }

      let formattedSeoSetting = [];
      if (Array.isArray(seoSetting)) {
        formattedSeoSetting = seoSetting.map((item) => {
          if (!pageId) {
            const { id, ...rest } = item;
            return rest;
          }
          return item;
        });
      } else if (seoSetting && typeof seoSetting === "object") {
        formattedSeoSetting = [
          {
            ...(pageId && formFor?.seo_setting?.[0]?.id
              ? { id: formFor.seo_setting[0].id }
              : {}),
            meta_title: seoSetting.meta_title || updatedTitle,
            meta_description: seoSetting.meta_description || "",
            meta_keywords: seoSetting.meta_keywords || "",
            indexing: seoSetting.indexing ?? true,
          },
        ];
      }

      const payload = {
        title: updatedTitle || title,
        slug: fullSlug || slug,
        group: group || formFor?.group || "page",
        status: status || formFor?.status || "ACTIVE",
        en: currentLang === "en" ? { data } : formFor?.en || null,
        bn: currentLang === "bn" ? { data } : formFor?.bn || null,
        seo_setting: formattedSeoSetting,
      };

      let response;
      if (pageId) {
        response = await api.put(
          endpoints.edit_cms_pages.replace("id", pageId),
          payload,
          true,
        );
      } else {
        response = await api.post(endpoints.add_cms_pages, payload, true);
      }

      if (
        response?.status === true ||
        response?.status === "Success" ||
        response?.data
      ) {
        message.success("CMS Page saved successfully!");
        getCMSPages();
        const savedData = response?.data || payload;
        setFormFor((prev) => ({
          ...(prev || {}),
          ...savedData,
        }));

        try {
          const cleanSlug = (savedData.slug || fullSlug || slug).replace(
            /^\/+|\/+$/g,
            "",
          );
          await api.post(
            endpoints.clear_cms_cache,
            {
              path: `/${cleanSlug}`,
              tag: `cms-${cleanSlug}`,
            },
            true,
          );
        } catch (e) {
          console.error("Failed to automatically clear cache on save", e);
        }

        const targetSlug = savedData.slug || fullSlug || slug;
        const targetGroup =
          savedData.group ||
          group ||
          (activeTab !== "all" ? activeTab : "page");

        // Refetch the page data directly from the backend to ensure the UI is fully updated
        if (targetSlug) {
          await getCMSSinglePages(targetSlug);
        }

        // A renamed slug leaves the old URL cached too.
        const previousSlug = formFor?.slug || formFor?.data?.slug;
        clearCacheAfterChange({
          slug: [targetSlug, previousSlug].filter(Boolean),
          group: String(targetGroup).toLowerCase(),
        });
        if (mode === "add") {
          setMode("Edit");
          navigate(
            `/edit-cms/${encodeURIComponent(targetSlug)}?group=${targetGroup}`,
            {
              replace: true,
            },
          );
        }
      } else {
        message.error(response?.message || "Failed to save page.");
      }
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.message || "Error saving CMS page.");
    }
  };

  const handleDeletePage = async (record) => {
    try {
      const id = record?.id;
      const api = requests();
      const response = await api.del(
        endpoints.delete_cms_pages.replace("id", id),
        {},
        true,
      );
      if (
        response?.status === true ||
        response?.status === "Success" ||
        response
      ) {
        message.success("Page deleted successfully.");
        getCMSPages();
        clearCacheAfterChange(cacheTarget(record));
      } else {
        message.error(response?.message || "Failed to delete page.");
      }
    } catch (error) {
      console.error(error);
      message.error("Error deleting page.");
    }
  };

  const handleCreatePage = async (values) => {
    try {
      setCreating(true);
      const api = requests();
      const newSlug = values.slug.toLowerCase().replace(/\s+/g, "-");
      const isBlogTab = activeTab === "blogs" || activeTab === "blog";
      const payload = {
        title: values.title,
        slug: newSlug,
        group: isBlogTab ? "blogs" : activeTab !== "all" ? activeTab : "page",
        status: "ACTIVE",
        en: {
          data: {
            sections: isBlogTab
              ? [
                  {
                    id: `sec-${Date.now()}`,
                    type: "BlogDetail",
                    title: "Blog Article & Details",
                    enabled: true,
                    data: {
                      title: values.title,
                      heading: values.title,
                      content: "",
                      excerpt: "",
                      author: "Infotainment Specialist",
                      category: "Repair Guides",
                      tags: ["repair", "infotainment"],
                    },
                  },
                ]
              : [],
          },
        },
        bn: null,
        seo_setting: [
          {
            meta_title: values.title,
            meta_description: "",
            meta_keywords: "",
            indexing: true,
          },
        ],
      };

      const response = await api.post(endpoints.add_cms_pages, payload, true);
      if (
        response?.status === true ||
        response?.status === "Success" ||
        response?.data
      ) {
        message.success(`Page "${values.title}" created successfully!`);
        setIsCreateModalOpen(false);
        createForm.resetFields();
        getCMSPages();
      } else {
        message.error(response?.message || "Could not create page.");
      }
    } catch (error) {
      console.error(error);
      message.error(
        error?.response?.data?.message || "Failed to create new page.",
      );
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (!mode) {
      getCMSPages(currentPage, pageSize, activeTab, searchTerm);
    }
  }, [activeTab, currentPage, pageSize, mode]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryGroup = queryParams.get("group");
    const pathname = location.pathname;

    if (queryGroup) {
      setActiveTab(toTabKey(queryGroup));
    } else if (pathname.includes("/blog")) {
      setActiveTab("blogs");
    }

    if (pathname.includes("/add-cms")) {
      setMode("add");
      setFormFor(null);
      setSlug("new-page");
      setTitle("New Page");
    } else if (
      pathname.includes("/edit-cms/") ||
      pathname.includes("/edit-pages/")
    ) {
      const routeSlug = params.slug || pathname.split("/").pop();
      if (routeSlug && routeSlug !== "edit-cms" && routeSlug !== "edit-pages") {
        setMode("Edit");
        setSlug(routeSlug);
        getCMSSinglePages(routeSlug);
      }
    } else {
      setMode("");
      setFormFor(null);
    }
  }, [location.pathname, location.search, params.slug]);

  // Filter pages by Active Tab and Search Term
  const filteredPages = pages.filter((page) => {
    if (page.slug === "login-no-more-available") {
      // Hidden way to see this page: search exactly for its slug or a secret phrase
      return searchTerm === "login-no-more-available" || searchTerm === "hidden-pages";
    }
    return true;
  }); // Server-side filtering already done for the rest

  const tabItems = CMS_TABS;

  const columns = getCMSColumns({
    handleToggleStatus,
    handleClearCache,
    handleDeletePage,
    clearingCache,
    activeTab,
    navigate,
  });

  return (
    <div>
      {mode ? (
        <EditCMSPage
          formFor={formFor}
          title={title}
          slug={slug}
          mode={mode}
          editLayout={editLayout}
          singlePageLoading={singlePageLoading}
          activeTab={activeTab}
          onBack={() => {
            const targetGroup = toTabKey(activeTab);
            navigate(`/cms-pages?group=${targetGroup}`);
          }}
        />
      ) : (
        <div>
          {/* Header Bar matching Tykkit layout */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
              marginBottom: 16,
              padding: "16px 0px",
              background: "#fff",
              borderRadius: 10,
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  fontWeight: 700,
                  fontSize: 22,
                  color: "#111827",
                }}
              >
                CMS Management
              </h2>
            </div>

            <Space wrap>
              <Input
                prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                placeholder="Search pages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onPressEnter={() =>
                  currentPage === 1 ? getCMSPages(1) : setCurrentPage(1)
                }
                style={{
                  width: 240,
                  borderRadius: 8,
                  borderStyle: "solid",
                  borderColor: "#d9d9d9",
                }}
                allowClear
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={() => getCMSPages()}
                loading={listLoading}
                style={{
                  borderRadius: 8,
                  borderStyle: "solid",
                  borderColor: "#d9d9d9",
                }}
              >
                Refresh
              </Button>
              <AuthChecker
                permissions={[roleAndPermission.PageManagement.update]}
              >
                <Popconfirm
                  title="Clear the website cache for all pages?"
                  onConfirm={() => handleClearCache(null)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    icon={<ClearOutlined />}
                    loading={clearingCache === "all"}
                    style={{
                      borderRadius: 8,
                      borderStyle: "solid",
                      borderColor: "#d9d9d9",
                    }}
                  >
                    Clear cache
                  </Button>
                </Popconfirm>
              </AuthChecker>
              <AuthChecker
                permissions={[roleAndPermission.PageManagement.create]}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const targetGroup =
                      activeTab !== "all" ? activeTab : "page";
                    navigate(`/add-cms?group=${targetGroup}`);
                  }}
                  style={{ borderRadius: 8, fontWeight: 600 }}
                >
                  Add
                </Button>
              </AuthChecker>
            </Space>
          </div>

          <div>
            {/* Category / Group Navigation Tabs */}
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setCurrentPage(1);
                navigate(`/cms-pages?group=${key}`);
              }}
              items={tabItems}
              style={{ marginBottom: 12 }}
            />

            {/* Table Listing matching Tykkit Management format */}
            <Table
              columns={columns}
              dataSource={filteredPages}
              rowKey="id"
              loading={listLoading}
              scroll={{ x: "max-content" }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                showTotal: (total) => `Total : ${total}`,
              }}
              onChange={(pagination) => {
                // A new page size restarts at page 1 so the request stays in range.
                const sizeChanged = pagination.pageSize !== pageSize;
                setPageSize(pagination.pageSize);
                setCurrentPage(sizeChanged ? 1 : pagination.current);
              }}
            />
          </div>
        </div>
      )}

      {/* Create New Page Modal */}
      <Modal
        title="Create New CMS Page"
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={creating}
        okText="Create Page"
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreatePage}>
          <Form.Item
            name="title"
            label="Page Title"
            rules={[{ required: true, message: "Please enter page title" }]}
          >
            <Input placeholder="e.g. Diagnostic & Encoding Services" />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Page URL Slug"
            rules={[{ required: true, message: "Please enter URL slug" }]}
          >
            <Input
              placeholder="e.g. diagnostic-encoding-services"
              addonBefore="/"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CMSlist;
